/**
 * Available Apps Slice
 * Manages the state for apps/tools visible to the current authenticated user.
 * Module-classified entries are always excluded (filtered at backend and by selector).
 *
 * Task 31 — Requirements: 5.5, 5.6, 5.7, 5.8
 */

import { createSlice } from '@reduxjs/toolkit';
import { SysAppDto } from '../../services/api/sysAppService';
import { fetchAvailableApps } from '../thunks/availableAppsThunks';
import { logoutSuccess, logoutFailure } from './authSlice';

export interface AvailableAppsState {
  apps: SysAppDto[];
  isLoading: boolean;
  error: string | null;
}

const initialState: AvailableAppsState = {
  apps: [],
  isLoading: false,
  error: null,
};

const availableAppsSlice = createSlice({
  name: 'availableApps',
  initialState,
  reducers: {
    /** Clears the current error message. */
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAvailableApps.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAvailableApps.fulfilled, (state, action) => {
        state.isLoading = false;
        state.apps = action.payload;
      })
      .addCase(fetchAvailableApps.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Clear apps on logout so stale data is never shown to the next user
    builder.addCase(logoutSuccess, () => initialState);
    builder.addCase(logoutFailure, () => initialState);
  },
});

export const { clearError } = availableAppsSlice.actions;
export default availableAppsSlice.reducer;
